import { useEffect, type ReactNode } from "react";

const SUPPORT_EMAIL = "contact@ridgepath.io";
const POLICY_EFFECTIVE_DATE = "August 4, 2026";

function Arrow({ direction = "right" }: { direction?: "right" | "up-right" }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="arrow-icon">
      {direction === "up-right" ? (
        <path d="M7 17 17 7M8 7h9v9" />
      ) : (
        <path d="m9 5 7 7-7 7" />
      )}
    </svg>
  );
}

function Brand() {
  return (
    <a className="brand" href="/" aria-label="Onward home">
      Onward
    </a>
  );
}

function SiteHeader() {
  return (
    <header className="site-header">
      <div className="header-inner">
        <Brand />
        <nav aria-label="Primary navigation" className="primary-nav">
          <a href="/#features">Features</a>
          <a href="/privacy">Privacy</a>
          <a href="/support">Support</a>
        </nav>
        <a className="ridgepath-link" href="https://www.ridgepath.io" rel="noreferrer">
          by RidgePath Technologies <Arrow direction="up-right" />
        </a>
        <a className="header-cta" href="/#availability">Google Play — coming soon</a>
      </div>
    </header>
  );
}

function SiteFooter() {
  return (
    <footer className="site-footer">
      <div className="footer-lead">
        <Brand />
        <p>Private, place-aware timekeeping for Android.</p>
      </div>
      <nav aria-label="Footer navigation" className="footer-links">
        <a href="/privacy">Privacy</a>
        <a href="/delete">Account deletion</a>
        <a href="/support">Support</a>
        <a href="/terms">Terms</a>
      </nav>
      <p className="footer-owner">A product maintained by RidgePath Technologies.</p>
      <p className="footer-copy">© {new Date().getFullYear()} RidgePath Technologies</p>
    </footer>
  );
}

function PhoneMockup({ screen, label, compact = false }: { screen: string; label: string; compact?: boolean }) {
  return (
    <div className={`phone ${compact ? "phone-compact" : ""}`} aria-label={label} role="img">
      <div className="phone-speaker" />
      <div className="phone-screen">
        <img src={screen} alt="" />
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section className="hero" aria-labelledby="hero-heading">
      <div className="clock-motif" aria-hidden="true">
        <span /><span /><span /><span /><span /><span /><span /><span />
      </div>
      <div className="hero-copy">
        <h1 id="hero-heading">Make time<br />feel tangible.</h1>
        <p>Count down to what is ahead.<br />Measure the moments that shaped you.</p>
        <div className="hero-actions">
          <a className="primary-button" href="#availability">Google Play — coming soon</a>
          <a className="text-link" href="#time-zones">See how time zones work <Arrow /></a>
        </div>
      </div>
      <div className="hero-product">
        <PhoneMockup screen="/app-home.png" label="Onward Android home screen with countdowns and milestones" />
      </div>
    </section>
  );
}

function TimeZones() {
  return (
    <section className="time-zone-section" id="time-zones" aria-labelledby="time-zone-heading">
      <div className="time-zone-copy">
        <h2 id="time-zone-heading">A moment belongs to<br />the place it happened.</h2>
        <p>Onward preserves the original local time and time zone, so birthdays, anniversaries, and milestones keep their meaning wherever life takes you.</p>
        <div className="time-arc" aria-label="The same moment shown in Berlin and New York">
          <div className="time-point berlin">
            <span className="city">Born in Berlin</span>
            <strong>June 14, 2018 · 8:42 AM</strong>
            <span className="zone">CEST</span>
          </div>
          <div className="arc-line" aria-hidden="true"><span /></div>
          <div className="time-point new-york">
            <span className="city">Viewed in New York</span>
            <strong>2:42 AM</strong>
            <span className="zone">EDT</span>
          </div>
        </div>
      </div>
      <div className="detail-product">
        <PhoneMockup compact screen="/app-detail.png" label="Onward event detail showing its original time zone" />
      </div>
    </section>
  );
}

function VisibilityRow({ icon, title, date, visibility, detail }: { icon: string; title: string; date: string; visibility: string; detail: string }) {
  return (
    <div className="visibility-row">
      <span className="visibility-icon" aria-hidden="true">{icon}</span>
      <span className="visibility-event"><strong>{title}</strong><small>{date}</small></span>
      <span className="visibility-state"><strong>{visibility}</strong><small>{detail}</small></span>
      <Arrow />
    </div>
  );
}

function PrivacySection() {
  return (
    <section className="privacy-section" id="privacy" aria-labelledby="privacy-heading">
      <div className="privacy-copy">
        <span className="lock-mark" aria-hidden="true">
          <svg viewBox="0 0 24 24"><path d="M7 10V7a5 5 0 0 1 10 0v3M6 10h12v10H6z" /></svg>
        </span>
        <h2 id="privacy-heading">Private by default.<br />Shared on purpose.</h2>
        <p>Your moments stay on your device. Family sharing will only happen when you choose it.</p>
        <a className="text-link" href="/privacy">Read our privacy approach <Arrow /></a>
      </div>
      <div className="visibility-list">
        <VisibilityRow icon="⛰️" title="Mountain weekend" date="Aug 22, 2026 · 4:00 PM EDT" visibility="Private" detail="Only you" />
        <VisibilityRow icon="✦" title="Sam’s graduation" date="May 16, 2027 · 10:00 AM EDT" visibility="Household" detail="You + 4 others" />
      </div>
    </section>
  );
}

const featureItems = [
  ["01", "Count both ways", "See the time ahead in days, hours, minutes, and seconds—or measure a milestone with calendar age and total elapsed time."],
  ["02", "Remind yourself", "Choose normal reminders or explicitly enable an exact event alarm. Each notification stays under your control."],
  ["03", "Built local-first", "Events remain useful offline. Optional Google Drive backup and Family sync add continuity without becoming a requirement to view your own moments."],
];

function Features() {
  return (
    <section className="features-section" id="features" aria-labelledby="features-heading">
      <div className="section-heading-row">
        <h2 id="features-heading">Timekeeping with context.</h2>
        <p>Thoughtful details for the dates that deserve more than a calendar square.</p>
      </div>
      <div className="feature-list">
        {featureItems.map(([number, title, copy]) => (
          <article className="feature-item" key={number}>
            <span>{number}</span>
            <h3>{title}</h3>
            <p>{copy}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function Availability() {
  return (
    <section className="availability-section" id="availability" aria-labelledby="availability-heading">
      <div>
        <h2 id="availability-heading">Onward is taking shape.</h2>
        <p>The Android app is in private development. Public Google Play testing and Family accounts are next.</p>
      </div>
      <a className="secondary-button" href={`mailto:${SUPPORT_EMAIL}?subject=Onward%20Android%20updates`}>Ask about Android access</a>
    </section>
  );
}

function LandingPage() {
  return <><SiteHeader /><main><Hero /><TimeZones /><PrivacySection /><Features /><Availability /></main><SiteFooter /></>;
}

type InfoSection = { heading: string; body: ReactNode };

function InfoPage({ title, intro, sections }: { title: string; intro: string; sections: InfoSection[] }) {
  return (
    <><SiteHeader /><main className="info-page">
      <header className="info-hero"><h1>{title}</h1><p>{intro}</p></header>
      <div className="info-content">
        {sections.map((section) => <section key={section.heading}><h2>{section.heading}</h2><div>{section.body}</div></section>)}
      </div>
    </main><SiteFooter /></>
  );
}

function PrivacyPage() {
  return <InfoPage title="Privacy should feel concrete." intro={`Effective ${POLICY_EFFECTIVE_DATE}. Onward is designed around data minimization, local ownership, and deliberate sharing.`} sections={[
    { heading: "Who operates Onward", body: <><p>Onward is an Android product maintained by RidgePath Technologies. This notice covers the private Android test, the Onward service at <code>onwardtime.app</code>, optional Google Drive backup, and Family synchronization.</p><p>Onward does not include advertising or third-party behavioral analytics.</p></> },
    { heading: "Data kept on your device", body: <p>Onward stores your events, notes, original time zones, repeat rules, reminder and alarm choices, organization settings, artwork choices, and any custom photo copy you deliberately select in the app’s private storage. Calendar import is an on-demand local read that creates a review draft from the title, time, time zone, and supported repeat rule; it does not copy attendees, location, description, account details, or calendar reminders.</p> },
    { heading: "Google account and managed service", body: <p>If you sign in, Onward stores your Google account identifier, verified email, display name, generated Onward account and device identifiers, app version, hashed session credentials, plan status, and the service records needed for account controls. Family sync stores events you place in managed storage, their private or household visibility, household membership and invitations, and each member’s private filing and notification preferences. Reminder times and exact-alarm choices remain personal and are not shared with the household.</p> },
    { heading: "Drive backup and push", body: <><p>Premium Drive backup is optional. Onward writes its backup to Google Drive’s hidden app-data folder, which is not browsable or shareable through normal Drive. You can disconnect it or use <strong>Delete Drive copy</strong> inside Onward.</p><p>Family activity push uses Firebase Cloud Messaging. Onward stores the device’s Firebase installation identifier encrypted at rest. Dispatch records contain identifiers and a generic activity type, not event titles, notes, artwork, reminders, or alarms.</p></> },
    { heading: "Sharing a moment", body: <p>Sharing is always user initiated. A countdown picture contains the event title, original event time and time zone, visual style, capture timestamp, and Onward branding. Its optional import link or <code>.onward</code> file contains only the title, time, time zone, repeat choice, symbol, and color. Notes, reminders, alarms, source event IDs, account IDs, and household access are excluded. Import links keep the event payload after the URL’s <code>#</code>, so it is not sent to the RidgePath website.</p> },
    { heading: "Notifications and alarms", body: <p>Onward requests notification permission in context when you choose a reminder or preview-expiration notice. Exact alarms are optional, configured independently by each member, and require Android’s separate Alarms &amp; reminders access. Delivery can be affected by device, operating-system, or network conditions, and Onward is not an emergency alert service.</p> },
    { heading: "Service providers", body: <p>Onward uses Cloudflare for the website, API, database, private object-storage foundation, queues, and operational logs. Google services provide account sign-in, optional Drive backup, Google Play billing and purchase verification, and Firebase Cloud Messaging. These providers process only the data needed for the feature you use under their own applicable terms and privacy notices.</p> },
    { heading: "Retention", body: <><p>Local data remains until you delete it or clear Onward’s app data. A Drive copy remains until you replace or delete it. Active managed account data remains while your account exists. Account deletion removes the account and account-owned service records after any household you organize is dissolved. Short-term Cloudflare disaster-recovery history may retain a recoverable copy until it expires on the provider’s service schedule; it is not used for normal app features.</p><p>Expired sign-in challenges are removed after one day; expired or revoked sessions after 30 days; revoked devices after 90 days; used or revoked invitations after 90 days; expired pending invitations after 30 days; delivered notification dispatch records after 14 days; and exhausted dispatch records after 30 days. Deleted managed event records, incremental sync history, ended household memberships, and dissolved households are retained for 365 days. A device returning with an older cursor receives an authoritative snapshot; an unsynced local edit missing from that snapshot is preserved as a private review copy instead of being silently discarded or re-shared. Google Play verification stores a one-way purchase-token fingerprint for the life of the Onward account to prevent replay and support restoration; the raw token is not stored.</p></> },
    { heading: "Your controls", body: <p>You can keep events device-only, choose whether an event is private or shared with your household, export or share selected event data, disconnect devices, delete the Drive copy, and delete the Onward cloud account inside the app. Cloud-account deletion leaves device-local dates in place and does not cancel a Google Play subscription. See <a href="/delete">account deletion</a> for exact steps. Privacy questions can be sent to <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.</p> },
    { heading: "Children and changes", body: <><p>Onward accounts in this Android milestone are intended for adults and are not directed to children under 13. An adult may record family dates, but child-owned accounts are not supported in the private test.</p><p>RidgePath will update this notice before a material data-practice change and post a new effective date here.</p></> },
  ]} />;
}

function SupportPage() {
  return <InfoPage title="Support for the moments that matter." intro="Onward is currently in private Android development under RidgePath Technologies." sections={[
    { heading: "Get help", body: <p>Email <a href={`mailto:${SUPPORT_EMAIL}?subject=Onward%20support`}>{SUPPORT_EMAIL}</a> with “Onward” in the subject. Please describe what you expected, what happened, and your Android version. Do not include sensitive event notes or account credentials.</p> },
    { heading: "Reminder troubleshooting", body: <p>Check that Android notifications are allowed for Onward. For an exact event alarm, also confirm that Alarms &amp; reminders access remains enabled. Reopening Onward rebuilds eligible schedules after a clock or time-zone change.</p> },
    { heading: "Time-zone questions", body: <p>Each event uses the time zone selected when the moment is created. The detail screen shows that original zone even while you travel. Edit the event if the recorded location or zone is incorrect.</p> },
  ]} />;
}

function DeletePage() {
  return <InfoPage title="Delete your Onward data." intro="Your control does not depend on keeping the app installed." sections={[
    { heading: "Delete the cloud account", body: <><p>In Onward, open <strong>Plans &amp; Settings</strong>, find <strong>Account control</strong>, choose <strong>Delete Onward cloud account</strong>, review the confirmation, and select <strong>Delete cloud account</strong>. If you organize a Family household, dissolve that household first so shared records are handled predictably.</p><p>This permanently deletes the managed account, cloud events, registered devices, and service entitlements. It signs the device out, but dates already stored locally on the phone remain.</p></> },
    { heading: "Delete device-local data", body: <p>Delete individual dates in Onward, or use Android’s app settings to clear Onward storage. Uninstalling also removes device-local application data according to Android behavior. Deleting only the cloud account does not remotely erase local dates from disconnected phones.</p> },
    { heading: "Delete the Drive copy", body: <p>Cloud-account deletion and Google Drive backup are separate. In <strong>Plans &amp; Settings</strong>, open <strong>Google Drive private backup</strong> and choose <strong>Delete Drive copy</strong> to permanently remove Onward’s hidden Drive file. Dates already on the device remain.</p> },
    { heading: "Cancel a subscription", body: <p>Deleting an Onward account does not cancel a Google Play subscription. Manage or cancel it separately in <a href="https://play.google.com/store/account/subscriptions" rel="noreferrer">Google Play subscriptions</a>. Onward also shows this distinction in the in-app deletion confirmation.</p> },
    { heading: "Request help", body: <p>If you cannot access the in-app control, email <a href={`mailto:${SUPPORT_EMAIL}?subject=Onward%20data%20deletion`}>{SUPPORT_EMAIL}</a> from the Google address associated with Onward. Do not send a password, sign-in token, invitation link, event notes, or purchase token. RidgePath may need to verify account ownership before acting.</p> },
  ]} />;
}

function TermsPage() {
  return <InfoPage title="Simple terms for an app in development." intro={`Effective ${POLICY_EFFECTIVE_DATE}. These preliminary terms cover the private Android test and will be reviewed before public release.`} sections={[
    { heading: "Private-test software", body: <p>RidgePath Technologies provides Onward for evaluation and personal, non-emergency use during private development. Features, storage formats, plan availability, and service behavior may change. Do not rely on a private-test build as the only copy of an important date or record.</p> },
    { heading: "Accounts and eligibility", body: <p>You are responsible for your Google account, connected devices, and activity under your Onward account. Onward accounts in this milestone are intended for adults; child-owned accounts are not supported. Do not attempt to bypass household seat, entitlement, security, or access controls.</p> },
    { heading: "Your events and sharing", body: <p>You keep responsibility for the event content you enter and for choosing who receives a picture, import file, invitation, or household-shared event. Only upload or share content you have the right to use. New managed events are private by default; selecting household visibility makes the event available to authorized household members.</p> },
    { heading: "Time, reminders, and alarms", body: <p>You are responsible for reviewing event times, source time zones, repeat rules, reminders, and alarms. Onward is not an emergency alert service. Device, operating-system, permission, time-zone, or network conditions may delay or prevent delivery.</p> },
    { heading: "Plans and subscriptions", body: <p>Private previews and future paid plans may unlock different features. Google Play will show the current localized price, billing period, trial or offer terms, and renewal details before purchase. Deleting an Onward account does not cancel a store subscription; cancellation must be completed through Google Play.</p> },
    { heading: "Service availability", body: <p>RidgePath works to protect and maintain Onward but does not promise uninterrupted private-test availability or that every feature will remain unchanged. To the extent permitted by applicable law, the private test is provided without warranties beyond those that cannot legally be excluded.</p> },
    { heading: "Privacy and contact", body: <p>The <a href="/privacy">privacy notice</a> explains Onward’s current data practices and controls. Onward is maintained by RidgePath Technologies. Questions about these preliminary terms can be sent to <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.</p> },
  ]} />;
}

function ImportPage() {
  const payload = window.location.hash.replace(/^#/, "");
  const validPayload = /^[A-Za-z0-9_-]{20,4096}$/.test(payload);
  const openInApp = validPayload ? `onward://import#${payload}` : null;

  return (
    <><SiteHeader /><main className="import-page">
      <section className="import-panel" aria-labelledby="import-heading">
        <span className="import-mark" aria-hidden="true">✦</span>
        <h1 id="import-heading">A moment was shared with you.</h1>
        <p>
          {validPayload
            ? "Open Onward to review the event and add a new private copy. Nothing is saved until you confirm inside the app."
            : "This import link is incomplete or no longer valid. Ask the sender to share the moment again from Onward."}
        </p>
        {openInApp && <a className="primary-button" href={openInApp}>Open in Onward</a>}
        <div className="import-privacy">
          <strong>Private by default</strong>
          <span>Shared copies exclude notes, alarms, reminders, account IDs, and family access.</span>
        </div>
        <a className="text-link" href="/">Learn about Onward <Arrow /></a>
      </section>
    </main><SiteFooter /></>
  );
}

function JoinPage() {
  const token = window.location.hash.replace(/^#/, "");
  const validToken = /^onw_i_[A-Za-z0-9_-]{43}$/.test(token);
  const openInApp = validToken ? `onward://join#${token}` : null;

  return (
    <><SiteHeader /><main className="import-page">
      <section className="import-panel" aria-labelledby="join-heading">
        <span className="import-mark" aria-hidden="true">✦</span>
        <h1 id="join-heading">Your Onward family invited you.</h1>
        <p>
          {validToken
            ? "Open Onward, sign in with your own account, and review the household before joining. The invitation expires after seven days and can be used once."
            : "This family invitation is incomplete, expired, or already used. Ask the organizer to create a new invitation from Onward."}
        </p>
        {openInApp && <a className="primary-button" href={openInApp}>Review in Onward</a>}
        <div className="import-privacy">
          <strong>Your private dates stay private</strong>
          <span>Joining only grants access to dates deliberately marked for the household. Reminder and alarm choices remain personal.</span>
        </div>
        <a className="text-link" href="/privacy">Read the privacy approach <Arrow /></a>
      </section>
    </main><SiteFooter /></>
  );
}

function NotFoundPage() {
  return <InfoPage title="That moment is not here." intro="The page you requested could not be found." sections={[
    { heading: "Continue", body: <p><a href="/">Return to the Onward home page</a> or visit <a href="/support">support</a>.</p> },
  ]} />;
}

export default function App() {
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  const pageTitles: Record<string, string> = {
    "/": "Onward Time — Make time feel tangible",
    "/privacy": "Privacy — Onward",
    "/support": "Support — Onward",
    "/delete": "Delete your data — Onward",
    "/terms": "Terms — Onward",
    "/import": "Open a shared moment — Onward",
    "/join": "Join an Onward family — Onward",
  };
  useEffect(() => {
    document.title = pageTitles[path] ?? "Page not found — Onward";
  }, [path]);
  if (path === "/") return <LandingPage />;
  if (path === "/privacy") return <PrivacyPage />;
  if (path === "/support") return <SupportPage />;
  if (path === "/delete") return <DeletePage />;
  if (path === "/terms") return <TermsPage />;
  if (path === "/import") return <ImportPage />;
  if (path === "/join") return <JoinPage />;
  return <NotFoundPage />;
}
